import { useState, useEffect, useRef } from 'react';
import { settingsAPI, zonesAPI, vpnStatusAPI } from '../../services/api';

const SMTP_SECURE_OPTIONS = ['ssl', 'tls', 'none'];

function VpnConnectionCard({ conn, onReconnect }) {
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectMsg, setReconnectMsg] = useState('');
  const isUp = conn.status === 'up';
  const isPending = !!Number(conn.config_pending);

  async function handleReconnect() {
    setReconnecting(true);
    setReconnectMsg('');
    try {
      const res = await vpnStatusAPI.reconnect(conn.connection_name);
      setReconnectMsg(res.message || 'Reconnect requested.');
      setTimeout(onReconnect, 20000);
    } catch (err) {
      setReconnectMsg(err.response?.data?.error || 'Failed to request reconnect.');
    } finally {
      setReconnecting(false);
    }
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-800">CRDB VPN Tunnel ({conn.connection_name})</h3>
        <span className={`rounded-full px-3 py-1 text-xs font-bold ${
          isPending ? 'bg-amber-100 text-amber-700'
            : isUp ? 'bg-emerald-100 text-emerald-700' : 'notice-blink bg-red-100 text-red-600'
        }`}>
          {isPending ? '● Pending setup' : isUp ? '● Connected' : '● Disconnected'}
        </span>
      </div>

      <div className="text-xs text-slate-500 space-y-0.5">
        {isPending ? (
          <p>Saved — the gateway will configure and initiate this tunnel within a minute.</p>
        ) : isUp ? (
          <p>Connected since {conn.established_since || '—'}</p>
        ) : (
          <p>Not connected — CRDB may have torn it down, or it needs re-initiating.</p>
        )}
        {conn.peer_ip && <p>Peer: {conn.peer_ip} · Remote source: {conn.remote_ts}</p>}
        <p>Last checked: {conn.last_checked_at || '—'} (auto-checked every minute)</p>
        {conn.last_action_result && (
          <p className="mt-1 font-mono text-[10px] text-slate-400 break-all">{conn.last_action_result}</p>
        )}
      </div>

      <button onClick={handleReconnect} disabled={reconnecting}
        className="mt-3 rounded-lg bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white px-3 py-1.5 text-xs font-semibold">
        {reconnecting ? 'Requesting…' : '⚡ Reconnect'}
      </button>
      {reconnectMsg && <p className="mt-1.5 text-xs text-slate-500">{reconnectMsg}</p>}
    </div>
  );
}

function AddVpnConnectionForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ connection_name: '', peer_ip: '', remote_ts: '', psk: '', institution_id: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    try {
      const res = await vpnStatusAPI.createConnection(form);
      setMsg(res.message || 'Saved.');
      setForm({ connection_name: '', peer_ip: '', remote_ts: '', psk: '', institution_id: '' });
      onCreated();
    } catch (err) {
      setMsg(err.response?.data?.error || 'Failed to save connection.');
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="rounded-lg border border-dashed border-slate-300 text-slate-500 hover:border-orange-400 hover:text-orange-600 px-3 py-2 text-xs font-semibold w-full">
        + Add production VPN connection
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">New VPN connection</h3>
        <button type="button" onClick={() => setOpen(false)} className="text-xs text-slate-400 hover:text-slate-600">Cancel</button>
      </div>
      <Field label="Connection name" name="connection_name" value={form.connection_name} onChange={handleChange}
        hint="e.g. crdb-prod — lowercase letters, digits and hyphens only." />
      <Field label="CRDB peer (gateway) IP" name="peer_ip" value={form.peer_ip} onChange={handleChange}
        hint="The IP CRDB gave you for their VPN endpoint (like 102.217.211.4 for UAT)." />
      <Field label="CRDB application server IP" name="remote_ts" value={form.remote_ts} onChange={handleChange}
        hint="The IP CRDB's server actually calls from — this is what gets allowed through the tunnel (like 102.217.211.193 for UAT)." />
      <Field label="Pre-shared key" name="psk" value={form.psk} onChange={handleChange} type="password"
        hint="The shared secret CRDB gave you for this tunnel." />
      <Field label="Institution ID (optional)" name="institution_id" value={form.institution_id} onChange={handleChange}
        hint="If different from the UAT institutionID above." />
      <button type="submit" disabled={saving}
        className="rounded-lg bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white px-3 py-1.5 text-xs font-semibold">
        {saving ? 'Saving…' : 'Save connection'}
      </button>
      {msg && <p className="text-xs text-slate-500">{msg}</p>}
    </form>
  );
}

function VpnStatusCard() {
  const [connections, setConnections] = useState([]);
  const [error, setError] = useState('');

  function load() {
    vpnStatusAPI.get()
      .then((res) => setConnections(res.connections || []))
      .catch(() => setError('Could not load VPN status.'));
  }

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-3">
      {error && <p className="text-xs text-red-600">{error}</p>}
      {connections.map((conn) => (
        <VpnConnectionCard key={conn.connection_name} conn={conn} onReconnect={load} />
      ))}
      <AddVpnConnectionForm onCreated={load} />
    </div>
  );
}

function Field({ label, name, value, onChange, type = 'text', hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={type === 'password' ? '(leave blank to keep current)' : ''}
        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
      />
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export default function SystemSettings() {
  const [activeTab, setActiveTab] = useState('email');
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');

  const [email, setEmail] = useState({
    smtp_host: '', smtp_port: '465', smtp_username: '', smtp_password: '',
    smtp_from_email: '', smtp_from_name: '', smtp_secure: 'ssl',
  });

  const [sms, setSms] = useState({
    sms_username: '', sms_password: '', sms_from: '',
  });

  const [crdb, setCrdb] = useState({ crdb_webhook_token: '', crdb_institution_id: '', crdb_institution_name: '', crdb_public_key: '' });

  const [payments, setPayments] = useState({ deposit_slip_admission_cutoff: '' });

  useEffect(() => {
    settingsAPI.get()
      .then(({ settings }) => {
        setEmail({
          smtp_host:       settings.smtp_host       ?? '',
          smtp_port:       settings.smtp_port       ?? '465',
          smtp_username:   settings.smtp_username   ?? '',
          smtp_password:   settings.smtp_password   ?? '',
          smtp_from_email: settings.smtp_from_email ?? '',
          smtp_from_name:  settings.smtp_from_name  ?? '',
          smtp_secure:     settings.smtp_secure     ?? 'ssl',
        });
        setSms({
          sms_username: settings.sms_username ?? '',
          sms_password: settings.sms_password ?? '',
          sms_from:     settings.sms_from     ?? '',
        });
        setCrdb({
          crdb_webhook_token:    settings.crdb_webhook_token    ?? '',
          crdb_institution_id:   settings.crdb_institution_id   ?? '',
          crdb_institution_name: settings.crdb_institution_name ?? '',
          crdb_public_key:       settings.crdb_public_key       ?? '',
        });
        setPayments({
          deposit_slip_admission_cutoff: settings.deposit_slip_admission_cutoff ?? '',
        });
      })
      .catch(() => setError('Failed to load settings.'))
      .finally(() => setLoading(false));
  }, []);

  const handleEmailChange = (e) => {
    setEmail(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSmsChange = (e) => {
    setSms(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCrdbChange = (e) => {
    setCrdb(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePaymentsChange = (e) => {
    setPayments(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const pemFileRef = useRef(null);
  const [pemFileName, setPemFileName] = useState('');
  const [pemFileError, setPemFileError] = useState('');

  const handlePemFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setPemFileError('');
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '').trim();
      if (!text.includes('BEGIN PUBLIC KEY') && !text.includes('BEGIN RSA PUBLIC KEY')) {
        setPemFileError('That file does not look like a valid PEM public key.');
        return;
      }
      setCrdb(prev => ({ ...prev, crdb_public_key: text }));
      setPemFileName(file.name);
    };
    reader.onerror = () => setPemFileError('Failed to read the file.');
    reader.readAsText(file);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = activeTab === 'email' ? email : activeTab === 'sms' ? sms : activeTab === 'payments' ? payments : crdb;
      await settingsAPI.update(payload);
      setSuccess('Settings saved successfully.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'email',    label: 'Email (SMTP)' },
    { id: 'sms',      label: 'SMS' },
    { id: 'crdb',     label: 'CRDB Integration' },
    { id: 'payments', label: 'Payments' },
    { id: 'zones',    label: 'Zones & Chapters' },
  ];

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6">
      <h1 className="text-2xl font-bold text-slate-800 mb-1">System Settings</h1>
      <p className="text-sm text-slate-500 mb-6">Configure email and SMS delivery credentials.</p>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-6">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => { setActiveTab(t.id); setError(''); setSuccess(''); }}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === t.id
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'zones' ? (
        <ZonesChaptersSection />
      ) : loading ? (
        <div className="text-center py-12 text-slate-400">Loading…</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 space-y-4">

          {activeTab === 'email' && (
            <>
              <Field label="SMTP Host"       name="smtp_host"       value={email.smtp_host}       onChange={handleEmailChange} hint="e.g. mail.yourdomain.com" />
              <Field label="SMTP Port"       name="smtp_port"       value={email.smtp_port}       onChange={handleEmailChange} hint="465 for SSL, 587 for TLS/STARTTLS" />
              <Field label="SMTP Username"   name="smtp_username"   value={email.smtp_username}   onChange={handleEmailChange} />
              <Field label="SMTP Password"   name="smtp_password"   value={email.smtp_password}   onChange={handleEmailChange} type="password" />
              <Field label="From Email"      name="smtp_from_email" value={email.smtp_from_email} onChange={handleEmailChange} hint="Address shown in From field" />
              <Field label="From Name"       name="smtp_from_name"  value={email.smtp_from_name}  onChange={handleEmailChange} hint="e.g. AWTF Portal" />
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Encryption</label>
                <select
                  name="smtp_secure"
                  value={email.smtp_secure}
                  onChange={handleEmailChange}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  {SMTP_SECURE_OPTIONS.map(o => (
                    <option key={o} value={o}>{o.toUpperCase()}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {activeTab === 'sms' && (
            <>
              <Field label="SMS Username"   name="sms_username" value={sms.sms_username} onChange={handleSmsChange} hint="messaging-service.co.tz username" />
              <Field label="SMS Password"   name="sms_password" value={sms.sms_password} onChange={handleSmsChange} type="password" />
              <Field label="Sender ID (From)" name="sms_from"   value={sms.sms_from}     onChange={handleSmsChange} hint="Displayed as sender name (e.g. TLS)" />
            </>
          )}

          {activeTab === 'crdb' && (
            <>
              <VpnStatusCard />
              <Field
                label="Institution ID"
                name="crdb_institution_id"
                value={crdb.crdb_institution_id}
                onChange={handleCrdbChange}
                hint="The unique institutionID CRDB assigns to AWTF (e.g. 8008). Leave blank until CRDB provides it — while blank, incoming institutionID is not checked."
              />
              <Field
                label="Institution Name"
                name="crdb_institution_name"
                value={crdb.crdb_institution_name}
                onChange={handleCrdbChange}
                hint="AWTF's registered name — for reference only, not validated on incoming requests."
              />
              <Field
                label="Webhook Token"
                name="crdb_webhook_token"
                value={crdb.crdb_webhook_token}
                onChange={handleCrdbChange}
                type="password"
                hint="Legacy shared secret — used only while no public key is set below. Only change this if CRDB confirms they are rotating it on their side too."
              />
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="block text-sm font-medium text-slate-700">CRDB Public Key (PEM)</label>
                  <button type="button" onClick={() => pemFileRef.current?.click()}
                    className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700 hover:bg-orange-100">
                    ⬆ Upload .pem file
                  </button>
                  <input ref={pemFileRef} type="file" accept=".pem,.txt,.key" className="hidden" onChange={handlePemFile} />
                </div>
                {pemFileName && (
                  <p className="mb-1 text-xs text-emerald-600">Loaded from {pemFileName} — review below, then click Save.</p>
                )}
                {pemFileError && (
                  <p className="mb-1 text-xs text-red-600">{pemFileError}</p>
                )}
                <textarea
                  name="crdb_public_key"
                  value={crdb.crdb_public_key}
                  onChange={handleCrdbChange}
                  rows={8}
                  placeholder="-----BEGIN PUBLIC KEY-----&#10;...&#10;-----END PUBLIC KEY-----"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <p className="mt-1 text-xs text-slate-400">
                  Upload or paste the .pem public key CRDB issues for the digital-signature scheme (SHA256withRSA). Once this is set,
                  every verify/pay request's token is verified as a real CRDB signature instead of the legacy shared secret above.
                </p>
              </div>
            </>
          )}

          {activeTab === 'payments' && (
            <>
              <div className="rounded-lg border border-slate-200 px-4 py-3">
                <label className="block text-sm font-medium text-slate-700">Deposit Slip Uploads — Admission Cutoff</label>
                <p className="mt-0.5 mb-3 text-xs text-slate-400">
                  This is a special allowance, not a standing feature for all members. Only members whose
                  Admission Date is on or after the date below will see the
                  "📎 Paid via Direct Deposit? Attach Proof" button on their own invoice. Leave blank to
                  hide this option for everyone.
                </p>
                <input
                  type="date"
                  name="deposit_slip_admission_cutoff"
                  value={payments.deposit_slip_admission_cutoff}
                  onChange={handlePaymentsChange}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                {payments.deposit_slip_admission_cutoff && (
                  <button type="button"
                    onClick={() => setPayments({ deposit_slip_admission_cutoff: '' })}
                    className="mt-2 text-xs font-semibold text-slate-500 hover:text-red-600">
                    ✕ Clear (disable for everyone)
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-400">
                Currently: <span className={`font-semibold ${payments.deposit_slip_admission_cutoff ? 'text-emerald-600' : 'text-red-600'}`}>
                  {payments.deposit_slip_admission_cutoff
                    ? `Visible to members admitted on/after ${payments.deposit_slip_admission_cutoff}`
                    : 'Disabled for everyone'}
                </span>
              </p>
            </>
          )}

          {error   && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2">{error}</p>}
          {success && <p className="text-sm text-emerald-700 bg-emerald-50 rounded-lg px-4 py-2">{success}</p>}

          <div className="pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full sm:w-auto bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white font-medium px-6 py-2.5 rounded-lg text-sm transition-colors"
            >
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Zones & Chapters management ──────────────────────────────────────────────

function ZonesChaptersSection() {
  const [zones, setZones]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [newZoneName, setNewZoneName] = useState('');
  const [newChapterName, setNewChapterName] = useState({});
  const [renaming, setRenaming] = useState(null); // { type: 'zone'|'chapter', id, value }

  const load = () => {
    setLoading(true);
    zonesAPI.list()
      .then(({ zones }) => setZones(zones))
      .catch(() => setError('Failed to load zones.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleAddZone = async (e) => {
    e.preventDefault();
    const name = newZoneName.trim();
    if (!name) return;
    setError('');
    try {
      await zonesAPI.create({ name });
      setNewZoneName('');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add zone.');
    }
  };

  const handleAddChapter = async (zoneId) => {
    const name = (newChapterName[zoneId] || '').trim();
    if (!name) return;
    setError('');
    try {
      await zonesAPI.createChapter(zoneId, { name });
      setNewChapterName((prev) => ({ ...prev, [zoneId]: '' }));
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add chapter.');
    }
  };

  const handleDeleteZone = async (zone) => {
    if (!window.confirm(`Delete zone "${zone.name}" and all ${zone.chapters.length} of its chapters?`)) return;
    setError('');
    try {
      await zonesAPI.delete(zone.id);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete zone.');
    }
  };

  const handleDeleteChapter = async (chapter) => {
    if (!window.confirm(`Delete chapter "${chapter.name}"?`)) return;
    setError('');
    try {
      await zonesAPI.deleteChapter(chapter.id);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete chapter.');
    }
  };

  const saveRename = async () => {
    const { type, id, value } = renaming;
    const name = value.trim();
    if (!name) { setRenaming(null); return; }
    setError('');
    try {
      if (type === 'zone') await zonesAPI.update(id, { name });
      else await zonesAPI.updateChapter(id, { name });
      setRenaming(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to rename.');
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-400">Loading…</div>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Zones group chapters together. Members choose their chapter from this list when editing their profile.
      </p>

      {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2">{error}</p>}

      <form onSubmit={handleAddZone} className="flex gap-2">
        <input
          value={newZoneName}
          onChange={(e) => setNewZoneName(e.target.value)}
          placeholder="New zone name (e.g. Southern)"
          className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        <button type="submit" className="bg-orange-600 hover:bg-orange-500 text-white font-medium px-4 py-2 rounded-lg text-sm transition-colors">
          Add Zone
        </button>
      </form>

      <div className="space-y-3">
        {zones.map((zone) => (
          <div key={zone.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between bg-slate-50 px-4 py-2.5">
              {renaming?.type === 'zone' && renaming.id === zone.id ? (
                <input
                  autoFocus
                  value={renaming.value}
                  onChange={(e) => setRenaming((r) => ({ ...r, value: e.target.value }))}
                  onBlur={saveRename}
                  onKeyDown={(e) => e.key === 'Enter' && saveRename()}
                  className="flex-1 border border-slate-300 rounded-lg px-2 py-1 text-sm mr-2"
                />
              ) : (
                <span className="font-semibold text-slate-800 text-sm">{zone.name}</span>
              )}
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => setRenaming({ type: 'zone', id: zone.id, value: zone.name })}
                  className="text-xs font-semibold text-slate-500 hover:text-slate-800"
                >
                  Rename
                </button>
                <button
                  onClick={() => handleDeleteZone(zone)}
                  className="text-xs font-semibold text-red-500 hover:text-red-700"
                >
                  Delete
                </button>
              </div>
            </div>

            <div className="p-4 space-y-2">
              {zone.chapters.map((chapter) => (
                <div key={chapter.id} className="flex items-center justify-between px-3 py-1.5 rounded-lg hover:bg-slate-50">
                  {renaming?.type === 'chapter' && renaming.id === chapter.id ? (
                    <input
                      autoFocus
                      value={renaming.value}
                      onChange={(e) => setRenaming((r) => ({ ...r, value: e.target.value }))}
                      onBlur={saveRename}
                      onKeyDown={(e) => e.key === 'Enter' && saveRename()}
                      className="flex-1 border border-slate-300 rounded-lg px-2 py-1 text-sm mr-2"
                    />
                  ) : (
                    <span className="text-sm text-slate-700">{chapter.name}</span>
                  )}
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => setRenaming({ type: 'chapter', id: chapter.id, value: chapter.name })}
                      className="text-xs font-medium text-slate-400 hover:text-slate-700"
                    >
                      Rename
                    </button>
                    <button
                      onClick={() => handleDeleteChapter(chapter)}
                      className="text-xs font-medium text-red-400 hover:text-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}

              <div className="flex gap-2 pt-1">
                <input
                  value={newChapterName[zone.id] || ''}
                  onChange={(e) => setNewChapterName((prev) => ({ ...prev, [zone.id]: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddChapter(zone.id))}
                  placeholder="New chapter name"
                  className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <button
                  onClick={() => handleAddChapter(zone.id)}
                  className="text-xs font-semibold text-orange-600 hover:text-orange-700 px-3"
                >
                  + Add Chapter
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
